import { useState, type FormEvent } from "react";
import { PLANS, type Plan } from "../../../shared/types.ts";
import { Button, Dialog, Field, Input } from "../../ui/index.ts";
import { deleteAccount, errorText, getBilling } from "../../lib/api.ts";
import { useApp } from "../../lib/ctx.ts";
import { Row } from "./parts.tsx";

const WORD = "DELETE";

/** The Account section's last row: deletes the account and everything in it once the user types DELETE. */
export function DeleteAccount() {
  const app = useApp();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState<Plan>("free");
  const [error, setError] = useState<string | null>(null);
  const ready = typed.trim() === WORD;

  const show = (next: boolean) => {
    if (busy) return;
    setOpen(next);
    setTyped("");
    setError(null);
    if (next) getBilling().then((b) => setPlan(b.plan), () => {});
  };

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAccount();
      app.toast("Your account is deleted.", "ok");
      app.signOut();
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "Your account wasn't deleted. Try again."));
    }
  };

  return (
    <>
      <Row label="Delete account" hint="Removes your account and everything in it.">
        <Button variant="secondary" className="text-bad-ink" onClick={() => show(true)}>
          Delete account
        </Button>
      </Row>
      <Dialog
        open={open}
        onOpenChange={show}
        title="Delete your account?"
        description={
          <>
            Your profile, resume, swipes and applications go for good.
            {plan !== "free" && ` Your ${PLANS[plan].name} plan ends now, with no refund.`} Applications already sent stay with the employers.
          </>
        }
      >
        <form onSubmit={confirm} className="mt-5 flex flex-col gap-5">
          <Field label={`Type ${WORD} to confirm`}>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoCapitalize="characters" spellCheck={false} autoFocus />
          </Field>
          {error ? (
            <p role="alert" className="text-[13px] font-semibold text-bad-ink">
              {error}
            </p>
          ) : null}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => show(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" loading={busy} disabled={!ready || busy}>
              Delete account
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
